// src/components/ProtectedRoute.jsx
import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import LoadingSpinner from './LoadingSpinner';
import { useFeatures } from '../contexts/FeaturesContext';
import { clesLicence } from '../config/licenceModules';
import FeatureGate from './FeatureGate';

/**
 * Garde de route. Deux conditions distinctes, vérifiées dans cet ordre :
 *
 *   1. la LICENCE de l'école — le module a-t-il été souscrit ?
 *   2. la PERMISSION        — cet employé a-t-il le droit d'y entrer ?
 *
 * L'ordre compte pour le message affiché. Un directeur qui a tous les droits
 * sur la cantine, dans une école qui n'a pas souscrit la cantine, doit lire
 * « module non inclus dans votre licence » et non « accès refusé » : ce n'est
 * pas son administrateur qu'il doit aller voir, et le second message
 * l'enverrait sur une fausse piste.
 *
 * ⚠️ Ni l'un ni l'autre ne protège les données : c'est le serveur qui refuse
 * (`requireFeature` pour la licence, `requirePermission` pour les droits). Cet
 * écran évite une page cassée, il ne remplace pas la garde serveur.
 *
 * `requiredFeature` sert quand les deux découpages ne coïncident pas. L'emploi
 * du temps et la vie scolaire relèvent de la permission `ELEVES` — ils vivent
 * dans le dossier élève — mais se vendent séparément. Sans cette surcharge, une
 * école sans l'emploi du temps y accéderait quand même.
 */
const ProtectedRoute = ({ children, requiredModule, requiredFeature, showAccessDenied = true }) => {
  const { hasPermission, loading } = usePermissions();
  const { chargement: chargementLicence, auMoinsUne } = useFeatures();

  if (loading || chargementLicence) {
    // Le spinner du projet (LoadingSpinner), et non un cercle ecrit a la main.
    // Cette attente est la plus affichee de l'application : elle garde les 52
    // declarations de routes de App.js.
    return <LoadingSpinner message="Vérification des permissions…" size="lg" />;
  }

  // 1. La licence de l'école. Un module du socle n'a pas de clé : rien à vérifier.
  const cles = requiredFeature
    ? (Array.isArray(requiredFeature) ? requiredFeature : [requiredFeature])
    : clesLicence(requiredModule);
  if (cles.length > 0 && !auMoinsUne(cles)) {
    return <FeatureGate cles={cles}>{children}</FeatureGate>;
  }

  // 2. Les droits de l'employé.
  if (!hasPermission(requiredModule)) {
    if (!showAccessDenied) {
      return <Navigate to="/access-denied" replace />;
    }

    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center p-8 bg-red-50 rounded-lg border border-red-200">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-red-800 mb-2">Accès refusé</h2>
          <p className="text-red-600">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </p>
          <p className="text-sm text-red-500 mt-2">
            Module requis : {requiredModule}
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
